import { IPageData, IPageDataV2, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { TLoadDependeciesParams } from '@df/catalog/model';
import { NgInject } from '@df/ng/ng-inject';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import uniq from 'lodash-es/uniq';
import { NCmsBlogCategory } from '../blog/category/cms-blog-category.interface';
import { CmsBlogGroupNavigationService } from '../blog/group/cms-blog-group-navigation.service';
import { NCmsBlogPost } from '../blog/post/cms-blog-post.interface';
import { NCmsBlogRoot } from '../blog/root/cms-blog-root.interface';
import { ICmsPageResourceDecorator } from './icms-model.resource.decorator';

export class ICmsPageDecorator<T extends IPageData = IPageData> extends ICmsPage<T> {
    @NgInject(CmsBlogGroupNavigationService)
    private cmsBlogGroupNavigationService!: CmsBlogGroupNavigationService;

    /**
     * Linked posts by tag
     * blog-category type only
     */
    private tagLinkedPosts?: ICmsPageDecorator<IPageDataV2>[] | undefined;

    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     */
    get blogPostCategories(): ICmsPageDecorator<IPageDataV2>[] | undefined {
        const ids = this.blogPostCategoryIds;

        return ids && (this.resources.page.getAllExisting(ids) as ICmsPageDecorator<IPageDataV2>[]);
    }

    /**
     * Retrieves categories (tags) ids of a blog post
     * blog-post type only
     */
    get blogPostCategoryIds(): number[] | undefined {
        return undefined;
    }

    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     * @returns
     */
    get blogCategoryPosts(): ICmsPageDecorator<IPageDataV2>[] | undefined {
        if (this.verbosity < 2) {
            return undefined;
        }

        switch (this.data?.pageType) {
            case NCmsBlogCategory.typeId:
                return (uniq([...(this.children || []), ...(this.tagLinkedPosts || [])]) as ICmsPageDecorator<IPageDataV2>[]).filter(
                    i => i.data?.pageType === NCmsBlogPost.typeId
                );
            case NCmsBlogRoot.typeId:
                return this.resources.page
                    .getAllExisting()
                    .filter(i => i.data?.pageType === NCmsBlogPost.typeId) as ICmsPageDecorator<IPageDataV2>[];
        }

        return undefined;
    }

    get showRelated(): boolean {
        return !!(this.data as IPageDataV2)?.typeData?.show_related;
    }

    loadRelatedArticles(params?: TLoadDependeciesParams): Promise<ICmsPageDecorator<IPageDataV3>[]> {
        return new Promise<ICmsPageDecorator<IPageDataV3>[]>(resolve => {
            if (this.data?.parent) {
                const parent = this.data?.parent;
                const pageType = this.data?.pageType;
                this.resources.page
                    .find(
                        {
                            verbosity: 3,
                            limit: 3,
                            sort: { 'typeData.date': -1 },
                            filter: { parent, pageType, id: { $ne: this.data.id } }
                        },
                        params
                    )
                    .then(pages => {
                        resolve(pages as ICmsPageDecorator<IPageDataV3>[]);
                    })
                    .catch(() => {
                        resolve([]);
                    });
            } else {
                resolve([]);
            }
        });
    }

    /**
     * Extra dependencies
     */
    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);

        const loader = params.loader;
        const safeParams = { ...params, safe: true };

        if (this.verbosity > 1) {
            if (this.rawData?.pageType === NCmsBlogPost.typeId) {
                // for blog post load all categories
                loader.add(this.loadBlogPostCategories(safeParams));
            }
        }

        if (this.verbosity > 2) {
            switch (this.rawData?.pageType) {
                case NCmsBlogPost.typeId:
                    loader.add(this.cmsBlogGroupNavigationService.loadPageCreatedNav(undefined, this as ICmsPageDecorator<IPageDataV3>));

                    if (this.showRelated) {
                        loader.add(this.loadRelatedArticles(safeParams));
                    }

                    break;
                case NCmsBlogCategory.typeId:
                    // for blog category load all posts
                    loader.add(this.loadBlogCategoryPosts(safeParams));
                    // for blog category load all blog categories
                    loader.add(
                        (this.resources.page as unknown as ICmsPageResourceDecorator).loadBlogCategories(this.verbosity, safeParams)
                    );
                    // for blog category load root page too
                    loader.add((this.resources.page as unknown as ICmsPageResourceDecorator).loadBlogRoot(this.verbosity, safeParams));

                    break;
                case NCmsBlogRoot.typeId:
                    // for blog category load all posts
                    loader.add((this.resources.page as unknown as ICmsPageResourceDecorator).loadBlogPosts(this.verbosity, safeParams));
                    // for blog category load all blog categories
                    loader.add(
                        (this.resources.page as unknown as ICmsPageResourceDecorator).loadBlogCategories(this.verbosity, safeParams)
                    );

                    break;
            }
        }
    }

    /**
     * Loads categories (tags) of a blog post
     * blog-post type only
     */
    private loadBlogPostCategories(params: TLoadDependeciesParams): Promise<ICmsPageDecorator<IPageDataV2>[]> | void {
        const categoryIds = this.blogPostCategoryIds;

        if (categoryIds) {
            return this.resources.page.loadAllIds(categoryIds, 2, params) as Promise<ICmsPageDecorator<IPageDataV2>[]>;
        }
    }

    /**
     * Loads posts linked to a blog category (including linked by tags entry)
     * blog-category type only
     */
    private async loadBlogCategoryPosts(params: TLoadDependeciesParams): Promise<unknown> {
        const pages = await this.resources.page.find({ filter: { 'typeData.tags': this.id }, verbosity: 2 });

        this.tagLinkedPosts = pages as ICmsPageDecorator<IPageDataV2>[];

        return await this.loadChildren(2, params);
    }
}
